import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot, Router, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { inject, Injector } from '@angular/core';
import { filter } from 'rxjs/operators';

export interface ReusableComponent {
  onReuseInit?: () => void;
  onReuseDetach?: () => void;
  onReuseDestroy?: () => void;
}

export class CustomReuseStrategy implements RouteReuseStrategy {
  private storedHandles = new Map<string, DetachedRouteHandle>();
  private readonly reuseEnabled = true; // TESTING - set back to false after debug

  // Track "just retrieved" handle to survive Angular's multiple shouldAttach calls
  private justRetrievedKey: string | null = null;
  private justRetrievedHandle: DetachedRouteHandle | null = null;

  // Debug tracking (can be removed later)
  private shouldAttachCounts = new Map<string, number>();

  private injector: Injector;
  private navigationSubscribed = false;

  constructor() {
    this.injector = inject(Injector);
  }

  private subscribeToNavigation(): void {
    if (this.navigationSubscribed) return;
    this.navigationSubscribed = true;
    try {
      const router = this.injector.get(Router);
      router.events.pipe(
        filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError)
      ).subscribe(() => {
        console.log('[REUSE-DEBUG] Navigation finished, clearing justRetrieved');
        this.justRetrievedKey = null;
        this.justRetrievedHandle = null;
      });
    } catch (e) {
      console.warn('CustomReuseStrategy: Could not inject Router for navigation tracking');
    }
  }

  private ensureRouteTitle(route: ActivatedRouteSnapshot | null | undefined): void {
    const routeConfig = route?.routeConfig;
    if (routeConfig && routeConfig.title === undefined) {
      routeConfig.title = routeConfig.path ?? 'app-route';
    }
  }

  private getRouteKey(route: ActivatedRouteSnapshot | null | undefined): string | null {
    if (!route) {
      return null;
    }
    const outlet = route.outlet ?? 'primary';
    const segments = route.pathFromRoot
      .map(r => r.url.map(u => u.path).join('/'))
      .filter(part => !!part);
    const fullPath = segments.join('/') || route.routeConfig?.path || '';
    if (!fullPath && outlet === 'primary') {
      return null;
    }
    return `${outlet}:${fullPath}`;
  }

  private isDestroyed(handle: DetachedRouteHandle | null): boolean {
    if (!handle) {
      return true;
    }
    const componentRef = (handle as any)?.componentRef;
    const hostView = componentRef?.hostView as any;
    return !!hostView?.destroyed;
  }

  private callHook(handle: DetachedRouteHandle | null, hook: keyof ReusableComponent) {
    const componentRef = (handle as any)?.componentRef;
    const instance: ReusableComponent | undefined = componentRef?.instance;
    const hookFn = instance?.[hook];
    if (typeof hookFn === 'function') {
      try {
        hookFn.call(instance);
      } catch (error) {
        console.error(`CustomReuseStrategy ${hook} hook failed`, error);
      }
    }
  }

  private callInitHook(handle: DetachedRouteHandle | null) {
    this.callHook(handle, 'onReuseInit');
  }

  private callDetachHook(handle: DetachedRouteHandle | null) {
    this.callHook(handle, 'onReuseDetach');
  }

  private callDestroyHook(handle: DetachedRouteHandle | null) {
    this.callHook(handle, 'onReuseDestroy');
    const componentRef = (handle as any)?.componentRef;
    componentRef?.destroy?.();
  }

  private clearJustRetrievedIfKeyChanged(key: string | null): void {
    if (key && this.justRetrievedKey && key !== this.justRetrievedKey) {
      console.log('[REUSE-DEBUG] clearing stale justRetrieved (was:', this.justRetrievedKey, 'now:', key, ')');
      this.justRetrievedKey = null;
      this.justRetrievedHandle = null;
    }
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    this.ensureRouteTitle(route);
    if (!this.reuseEnabled) {
      return false;
    }
    const path = route.routeConfig?.path ?? '';
    const shouldDetach = !!path && (
      path.startsWith('all-leads') ||
      path.startsWith('inactive-leads') ||
      path.startsWith('inbox') ||
      path.startsWith('opportunity-list') ||
      path.startsWith('dashboard')
    );
    if (shouldDetach) console.log('[REUSE-DEBUG] shouldDetach:', path);
    return shouldDetach;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.ensureRouteTitle(route);
    const key = this.getRouteKey(route);
    if (!key || !handle) {
      return;
    }
    const existingHandle = this.storedHandles.get(key);
    if (existingHandle && existingHandle !== handle) {
      console.log('[REUSE-DEBUG] store: REPLACING old handle for', key);
      this.callDestroyHook(existingHandle);
    }
    console.log('[REUSE-DEBUG] store:', key);
    this.callDetachHook(handle);
    this.storedHandles.set(key, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    this.ensureRouteTitle(route);
    const key = this.getRouteKey(route);
    if (!key) return false;

    // Clear stale justRetrieved if navigating to a different route
    this.clearJustRetrievedIfKeyChanged(key);

    // Check if this key was just retrieved (handle Angular's duplicate calls)
    if (key === this.justRetrievedKey && this.justRetrievedHandle && !this.isDestroyed(this.justRetrievedHandle)) {
      console.log('[REUSE-DEBUG] shouldAttach: true (justRetrieved fallback) for', key);
      return true;
    }

    const handle = this.storedHandles.get(key);
    const currentCount = (this.shouldAttachCounts.get(key) ?? 0) + 1;
    this.shouldAttachCounts.set(key, currentCount);
    console.log('[REUSE-DEBUG] shouldAttach:', key, {
      hasHandle: !!handle,
      destroyed: this.isDestroyed(handle),
      count: currentCount,
      justRetrievedKey: this.justRetrievedKey
    });
    if (!handle) return false;
    if (this.isDestroyed(handle)) {
      this.storedHandles.delete(key);
      return false;
    }
    console.log('[REUSE-DEBUG] shouldAttach: true for', key);
    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    this.ensureRouteTitle(route);
    const key = this.getRouteKey(route);
    if (!key) return null;

    // Clear stale justRetrieved if navigating to a different route
    this.clearJustRetrievedIfKeyChanged(key);

    // If this key was just retrieved, return the cached handle (don't call init hook again)
    if (key === this.justRetrievedKey && this.justRetrievedHandle && !this.isDestroyed(this.justRetrievedHandle)) {
      console.log('[REUSE-DEBUG] retrieve: returning justRetrieved handle for', key);
      return this.justRetrievedHandle;
    }

    const handle = this.storedHandles.get(key) || null;
    if (!handle) return null;
    if (this.isDestroyed(handle)) {
      this.storedHandles.delete(key);
      return null;
    }
    console.log('[REUSE-DEBUG] retrieve:', key);

    // Cache the handle for potential duplicate calls
    this.justRetrievedKey = key;
    this.justRetrievedHandle = handle;

    this.callInitHook(handle);
    this.storedHandles.delete(key);
    return handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    this.subscribeToNavigation();
    this.ensureRouteTitle(future);
    this.ensureRouteTitle(curr);
    return future.routeConfig === curr.routeConfig;
  }

  clearStoredHandles(): void {
    console.log('[REUSE-DEBUG] clearStoredHandles()');
    // Also clear justRetrieved state (account switch scenario)
    this.justRetrievedKey = null;
    this.justRetrievedHandle = null;
    for (const [key, handle] of this.storedHandles.entries()) {
      this.callDestroyHook(handle);
      this.storedHandles.delete(key);
    }
  }
}
